"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getQueryParameters = getQueryParameters;
exports.createQueryString = createQueryString;
exports.useURLState = useURLState;
exports.useLocationState = useLocationState;
exports.getLocationState = getLocationState;
const typed_dictionary_1 = require("@sixfold/typed-dictionary");
const typed_primitives_1 = require("@sixfold/typed-primitives");
const query_string_1 = require("query-string");
const react_1 = require("react");
// @ts-expect-error useHistory is not present in v7
const react_router_1 = require("react-router");
const useNavigateOrHistory = react_router_1.useNavigate ?? react_router_1.useHistory ?? (() => undefined);
function isNavigateFunction(navigate) {
    return navigate !== undefined && typeof navigate === 'function';
}
function getQueryParameters(search) {
    const parsedQueryString = (0, query_string_1.parse)(search);
    return Object.keys(parsedQueryString).reduce((memo, paramKey) => {
        const paramValue = parsedQueryString[paramKey];
        if (Array.isArray(paramValue)) {
            return {
                ...memo,
                [paramKey]: paramValue.shift() ?? undefined,
            };
        }
        if (paramValue === null) {
            return {
                ...memo,
                [paramKey]: undefined,
            };
        }
        return {
            ...memo,
            [paramKey]: paramValue,
        };
    }, {});
}
function createQueryString(queryStringParams) {
    return (0, query_string_1.stringify)(queryStringParams, { arrayFormat: 'comma' });
}
/**
 * Use state (parameters) from location, i.e get the values present, and update them via history.push
 *
 * @param dictionary definition of parameters to extract from query string
 * @returns parsed query string and a function for updating it
 */
function useURLState(dictionary) {
    const historyOrNavigate = useNavigateOrHistory();
    const location = (0, react_router_1.useLocation)();
    const parsedQuery = (0, react_1.useMemo)(() => {
        const params = getQueryParameters(location.search);
        return (0, typed_dictionary_1.Dictionary)(dictionary).parse(params);
    }, [location, dictionary]);
    const updateQuery = (0, react_1.useCallback)((value) => {
        if (historyOrNavigate === undefined) {
            return;
        }
        const updatedLocation = {
            pathname: location.pathname,
            hash: location.hash,
            search: createQueryString({
                ...getQueryParameters(location.search),
                ...(0, typed_primitives_1.objectMap)(value, (key, value) => dictionary[key].serialize(value)),
            }),
        };
        if (isNavigateFunction(historyOrNavigate)) {
            Promise.resolve(historyOrNavigate(updatedLocation)).catch(console.error);
            return;
        }
        historyOrNavigate.push(updatedLocation);
    }, [historyOrNavigate, location.pathname, location.hash, location.search]);
    return (0, react_1.useMemo)(() => [parsedQuery, updateQuery], [parsedQuery, updateQuery]);
}
/**
 * Hook for getting a typed value from location state, updates as location updates
 */
// eslint-disable-next-line @typescript-eslint/ban-types
function useLocationState() {
    const location = (0, react_router_1.useLocation)();
    return getLocationState(location);
}
/**
 * Access the location state in a typed way, return value is partial as the state may not include
 * all of the properties
 *
 * @param location location to read the state from
 * @returns Typed state from location
 */
// eslint-disable-next-line @typescript-eslint/ban-types
function getLocationState(location) {
    return (location.state ?? {});
}
//# sourceMappingURL=use_url_state.js.map